/*
  Admin › Settings (26) — wave 1: ideas 1 (Branding / Shop Drawing Disciplines sections, two-column rows),
  4 (sticky unsaved-changes bar; leaving the tab or the page asks first — guard lives in AdminPage),
  7 (add a discipline with a "will be saved as" preview + suggested chips).
  Wave 2: ideas 2 (theme cards with thumbnails; picking one previews it for this admin only until saved)
  and 3 (each discipline shows how many drawings carry it; removing one in use asks first, in a popover).
  Wave 3: idea 8 (Workspace defaults: time zone, currency, work week, date format — saved with the rest,
  in the same unsaved bar; pages read them through useWorkspace()). Each hint says where the value is used:
  the time zone and the date format by every date in the app (lib/format.js configureFormatting, applied by
  AppSettingsContext), the work week by a project's Timeline auto-plan. Amounts are labelled AED in column headers
  and server texts too, so the currency is still shown read-only with "Soon" (the API stores it) until
  workspaceCurrencyApplied() says money follows it — then it is a working select, "Default for BOQ, POs and invoices."
  Final QA: rows stack below 1280px (no squeezed controls); "Exit preview" in the ribbon under the top bar puts
  the draft theme back (registerStopPreview); the app name is checked where it is typed; a focused field and the
  bar's full summary stay readable above the sticky unsaved bar.
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, Check, Coins, Globe2, Loader2, Lock, LogOut, Plus, Tags, TriangleAlert, X } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import {
  DEFAULT_WORKSPACE, THEME_OPTIONS, normalizeShopDrawingDisciplines, useAppSettings, workspaceCurrencyApplied,
} from '@/contexts/AppSettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/ui/status-pill';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UnsavedBar } from '@/components/ui/unsaved-bar';
import { notify } from '@/components/ui/notify';
import { AdminCard, Segmented, SettingRow } from '@/components/admin/AdminUi';
import ThemePicker from '@/components/admin/ThemePicker';
import {
  FALLBACK_WORKSPACE_OPTIONS, WORKSPACE_FIELDS, disciplinePreview, settingsChanges, suggestedDisciplines,
} from '@/components/admin/adminModel';

const APP_NAME_MAX = 80;

const fromSettings = (settings = {}) => ({
  app_name: settings.app_name || '',
  theme: settings.theme || THEME_OPTIONS[0].value,
  shop_drawing_disciplines: normalizeShopDrawingDisciplines(settings.shop_drawing_disciplines || []),
  workspace: Object.fromEntries(WORKSPACE_FIELDS.map((field) => [field, settings.workspace?.[field] || DEFAULT_WORKSPACE[field]])),
});

/** A 320px select of one workspace setting (icon + label in the trigger). */
function SoonPill() {
  return <StatusPill tone="gray" label="Soon" size="sm" hideDot className="shrink-0" />;
}

function WorkspaceSelect({ id, icon: Icon, value, options, onChange, disabled, describe, testId }) {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger
        id={id}
        className="h-9 w-full max-w-[320px] rounded-control border-line bg-surface text-[13.5px] text-ink"
        data-testid={testId}
      >
        {/* a div, not a span: the trigger clamps its direct <span> children to one -webkit-box line */}
        <div className="flex min-w-0 items-center gap-2 truncate">
          <Icon className="h-[15px] w-[15px] shrink-0 text-ink-3" aria-hidden="true" />
          <SelectValue />
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-[320px]">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} data-testid={`${testId}-${option.value}`}>
            {describe ? describe(option) : option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** One active discipline: code · drawings count · remove (asks first when drawings carry it). */
function DisciplinePill({ code, count, isNew, onRemove }) {
  const [asking, setAsking] = useState(false);
  const fixed = code === 'OTHER';
  const inUse = !isNew && Number(count) > 0;
  const showCount = !isNew && count !== undefined && count !== null;
  const remove = () => {
    if (inUse) setAsking(true);
    else onRemove(code);
  };
  return (
    <Popover open={asking} onOpenChange={setAsking}>
      <PopoverAnchor asChild>
        <li
          className={cn(
            'inline-flex h-8 items-center gap-2 rounded-full border bg-surface pl-3 text-[13px] font-bold text-ink transition-colors',
            fixed ? 'pr-2.5' : 'pr-[5px]',
            isNew ? 'border-[color:var(--brand-soft-border)] bg-brand-soft' : 'border-line',
            asking && 'border-[color:var(--danger-border)]',
            'has-[button:hover]:border-[color:var(--danger-border)]'
          )}
          data-testid={`shop-drawing-discipline-${code}`}
        >
          {code}
          {isNew ? <span className="text-[11px] font-semibold text-brand-ink">new</span> : null}
          {showCount ? (
            <span
              className="rounded-full bg-[color:var(--status-gray-bg)] px-1.5 text-[11px] font-bold tabular-nums leading-[18px] text-ink-2"
              title={`${formatNumber(count)} ${Number(count) === 1 ? 'drawing' : 'drawings'} tagged ${code}`}
              data-testid={`discipline-count-${code}`}
            >
              <span className="sr-only">: </span>{formatNumber(count)}<span className="sr-only"> drawings</span>
            </span>
          ) : null}
          {fixed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-grid h-[22px] w-[22px] place-items-center text-ink-3" tabIndex={0} aria-label="OTHER can't be removed — it is the fallback">
                  <Lock className="h-3 w-3" aria-hidden="true" />
                </span>
              </TooltipTrigger>
              <TooltipContent>Always kept — drawings without a discipline use OTHER</TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={remove}
              aria-haspopup={inUse ? 'dialog' : undefined}
              aria-expanded={inUse ? asking : undefined}
              className={cn(
                'inline-grid h-[22px] w-[22px] place-items-center rounded-full text-ink-3 transition-colors hover:bg-status-red-bg hover:text-status-red-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                asking && 'bg-status-red-bg text-status-red-ink'
              )}
              aria-label={`Remove ${code}`}
              title={inUse ? `Remove ${code}…` : `Remove ${code} (saved only when you save)`}
              data-testid={`remove-shop-drawing-discipline-${code}`}
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </li>
      </PopoverAnchor>
      <PopoverContent
        side="bottom"
        align="start"
        role="dialog"
        aria-label={`Remove ${code}?`}
        className="w-[min(300px,calc(100vw-2rem))] rounded-xl border-line bg-surface p-3 text-ink shadow-overlay"
        data-testid={`discipline-remove-confirm-${code}`}
      >
        <div className="flex gap-2.5">
          <span className="ui-icon-tile tone-amber h-8 w-8 shrink-0" aria-hidden="true"><TriangleAlert className="h-4 w-4" /></span>
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold text-ink">Remove {code}?</p>
            <p className="mt-1 text-[12.5px] leading-relaxed text-ink-2">
              {formatNumber(count)} {Number(count) === 1 ? 'drawing is' : 'drawings are'} tagged {code}. They keep the tag,
              but {code} disappears from the discipline filter and the drawing form.
            </p>
          </div>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setAsking(false)} data-testid={`discipline-keep-${code}`}>Keep</Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => { setAsking(false); onRemove(code); }}
            data-testid={`discipline-remove-${code}`}
          >
            Remove {code}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** A focused field (and the lines under it) must not hide behind the sticky unsaved bar. */
function keepClearOfBar(node) {
  if (!node || typeof window === 'undefined') return;
  const control = node.closest?.('[data-setting-control]') || node;
  const bar = document.querySelector('[data-testid="unsaved-bar"]');
  if (!bar || !control.getBoundingClientRect) return;
  const box = control.getBoundingClientRect();
  const barBox = bar.getBoundingClientRect();
  if (!box.height || !barBox.height) return; // not laid out (tests, hidden tab)
  const overlap = box.bottom - (barBox.top - 12);
  if (overlap > 0) window.scrollBy({ top: overlap, behavior: 'smooth' });
}

export default function SettingsTab({ onDirtyChange }) {
  const {
    settings, settingsLoading, updateSettings, refreshSettings, setPreviewTheme, registerStopPreview,
  } = useAppSettings();
  const formRef = useRef(null);
  const [draft, setDraft] = useState(() => fromSettings(settings));
  const [touched, setTouched] = useState(false);
  const [newDiscipline, setNewDiscipline] = useState('');
  const [status, setStatus] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const [usage, setUsage] = useState(null);
  const [workspaceOptions, setWorkspaceOptions] = useState(null);

  const saved = useMemo(() => fromSettings(settings), [settings]);
  const savedThemeRef = useRef(saved.theme);
  savedThemeRef.current = saved.theme;

  // Settings arrive after the first render (and change after a save): follow them until the user edits.
  useEffect(() => {
    if (!touched) setDraft(saved);
  }, [saved, touched]);

  useEffect(() => {
    let alive = true;
    api.get('/admin/settings')
      .then((res) => {
        if (!alive) return;
        setUsage(res.data?.shop_drawing_usage || null);
        setWorkspaceOptions(res.data?.workspace_options || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const options = workspaceOptions || FALLBACK_WORKSPACE_OPTIONS;
  const changes = useMemo(() => settingsChanges(saved, draft, workspaceOptions), [saved, draft, workspaceOptions]);
  const dirtyCount = touched ? changes.length : 0;

  useEffect(() => {
    onDirtyChange?.(dirtyCount);
  }, [dirtyCount, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(0), [onDirtyChange]);

  // Theme preview: this browser only, while the draft theme differs from the saved one; gone on leave.
  const previewValue = touched && draft.theme !== saved.theme ? draft.theme : null;
  useEffect(() => { setPreviewTheme?.(previewValue); }, [previewValue, setPreviewTheme]);
  useEffect(() => () => setPreviewTheme?.(null), [setPreviewTheme]);
  // "Exit preview" in the ribbon under the top bar: the draft theme goes back to the saved one (other edits stay)
  useEffect(() => registerStopPreview?.(() => {
    setDraft((current) => ({ ...current, theme: savedThemeRef.current }));
  }), [registerStopPreview]);

  const edit = (patch) => {
    setTouched(true);
    setStatus((current) => (current === 'saving' ? current : 'idle'));
    setDraft((current) => ({ ...current, ...patch }));
  };

  const preview = disciplinePreview(newDiscipline);
  const alreadyActive = preview && draft.shop_drawing_disciplines.includes(preview);
  // the app name is checked where it is typed — Save waits until it has one
  const nameMissing = touched && !draft.app_name.trim();

  const addDiscipline = (value = newDiscipline) => {
    const token = disciplinePreview(value);
    if (!token || draft.shop_drawing_disciplines.includes(token)) return;
    edit({ shop_drawing_disciplines: normalizeShopDrawingDisciplines([...draft.shop_drawing_disciplines, token]) });
    if (value === newDiscipline) setNewDiscipline('');
  };

  const removeDiscipline = (code) => {
    if (code === 'OTHER') return;
    edit({ shop_drawing_disciplines: normalizeShopDrawingDisciplines(draft.shop_drawing_disciplines.filter((item) => item !== code)) });
  };

  const discard = () => {
    setDraft(saved);
    setTouched(false);
    setNewDiscipline('');
    setStatus('idle');
    setSaveError('');
  };

  const save = async () => {
    const name = draft.app_name.trim();
    if (!name) {
      document.getElementById('app-name')?.focus();
      return;
    }
    setStatus('saving');
    setSaveError('');
    try {
      const result = await updateSettings({
        app_name: name,
        theme: draft.theme,
        shop_drawing_disciplines: normalizeShopDrawingDisciplines(draft.shop_drawing_disciplines),
        workspace: draft.workspace,
      });
      setTouched(false);
      setStatus('saved');
      notify.success('Settings saved', result?.undo_id ? {
        description: changes.map((change) => change.text).join(' · '),
        undo: async () => {
          await api.post(`/undo/entries/${result.undo_id}`);
          await refreshSettings();
        },
      } : { description: changes.map((change) => change.text).join(' · ') });
    } catch (err) {
      setStatus('error');
      setSaveError(getErrorMessage(err, 'Could not save the settings'));
    }
  };

  const saving = status === 'saving';
  // 26 #8: the Currency select works once money in the app follows it (lib/format.js applies the workspace currency
  // and the AED headers read it — a filed request); until then it is read-only with "Soon", saying so honestly.
  const currencyApplied = workspaceCurrencyApplied();
  const editWorkspace = (field, value) => edit({ workspace: { ...draft.workspace, [field]: value } });
  const optionList = (field) => {
    const list = options[field] || [];
    // the saved value stays pickable even if an older list lacks it
    return list.some((option) => option.value === draft.workspace[field])
      ? list
      : [...list, { value: draft.workspace[field], label: draft.workspace[field] }];
  };
  const activeDisciplines = draft.shop_drawing_disciplines;
  const suggestions = suggestedDisciplines(activeDisciplines);
  const changeText = changes.map((change) => change.text).join(' · ');
  const barVisible = dirtyCount > 0;
  const onFieldFocus = useCallback((event) => {
    const { target } = event;
    // only the fields of this form — not a select's options, which React bubbles here from their portal
    if (!formRef.current?.contains(target)) return;
    window.requestAnimationFrame?.(() => keepClearOfBar(target));
  }, []);
  // the bar appears while a field is focused (e.g. right after "Add"): move the field above it
  useEffect(() => {
    if (!barVisible) return;
    const active = document.activeElement;
    if (active && formRef.current?.contains(active)) window.requestAnimationFrame?.(() => keepClearOfBar(active));
  }, [barVisible]);

  return (
    <div className="space-y-4" data-testid="app-settings-form" ref={formRef} onFocus={onFieldFocus}>
      <AdminCard
        title="Branding"
        subtitle="How the app looks and what it is called — for everyone."
        bodyClassName="divide-y divide-line-2 py-1"
        data-testid="settings-card-branding"
      >
        <SettingRow label="App name" hint="Shown in the browser tab and the app header." htmlFor="app-name">
          <div className="max-w-[420px]">
            <Input
              id="app-name"
              value={draft.app_name}
              onChange={(event) => edit({ app_name: event.target.value })}
              onKeyDown={(event) => {
                // Enter saves, as it did when this was a plain form
                if (event.key !== 'Enter' || event.nativeEvent?.isComposing) return;
                event.preventDefault();
                if (dirtyCount > 0 && !saving) save();
              }}
              maxLength={APP_NAME_MAX}
              className={cn(
                'h-9 rounded-control bg-surface text-[13.5px] text-ink',
                nameMissing ? 'border-[color:var(--danger-border)] focus-visible:ring-[color:var(--status-red)]' : 'border-line'
              )}
              data-testid="app-name-input"
              aria-describedby={nameMissing ? 'app-name-error app-name-count' : 'app-name-count'}
              aria-invalid={nameMissing || undefined}
              required
              disabled={settingsLoading}
            />
            <div className="mt-1 flex items-start gap-3 text-[12px]">
              {nameMissing ? (
                <p id="app-name-error" className="flex min-w-0 items-center gap-1 font-semibold text-status-red-ink" data-testid="app-name-error">
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Give the app a name — it shows in every browser tab
                </p>
              ) : null}
              <p id="app-name-count" className="ml-auto shrink-0 tabular-nums text-ink-3">
                {draft.app_name.length} / {APP_NAME_MAX}
              </p>
            </div>
          </div>
        </SettingRow>
        <SettingRow
          label={<span id="app-theme-label">Theme</span>}
          hint="Changes colours for everyone after you save. Pick one to preview it first."
        >
          <ThemePicker
            value={draft.theme}
            saved={saved.theme}
            onChange={(value) => edit({ theme: value })}
            disabled={settingsLoading}
            labelledBy="app-theme-label"
          />
        </SettingRow>
      </AdminCard>

      <AdminCard
        title="Shop Drawing Disciplines"
        subtitle="Drive the discipline filter and the drawing form on Shop Drawings."
        aside={usage ? (
          <span className="inline-flex items-center gap-1.5" data-testid="discipline-usage-total">
            <Tags className="h-3.5 w-3.5" aria-hidden="true" />
            {formatNumber(usage.total)} drawings tagged
          </span>
        ) : null}
        bodyClassName="divide-y divide-line-2 py-1"
        data-testid="settings-card-disciplines"
      >
        <SettingRow label="Active disciplines" hint="OTHER is always kept as the fallback. The number is how many drawings carry each one.">
          <TooltipProvider delayDuration={200}>
            <ul className="flex flex-wrap gap-2" aria-label="Active disciplines">
              {activeDisciplines.map((code) => (
                <DisciplinePill
                  key={code}
                  code={code}
                  count={usage ? usage.counts?.[code] ?? 0 : undefined}
                  isNew={!saved.shop_drawing_disciplines.includes(code)}
                  onRemove={removeDiscipline}
                />
              ))}
            </ul>
          </TooltipProvider>
        </SettingRow>
        <SettingRow label="Add a discipline" hint="Letters and numbers only — spaces become _." htmlFor="shop-drawing-discipline">
          <div className="flex max-w-[420px] gap-2">
            <Input
              id="shop-drawing-discipline"
              value={newDiscipline}
              onChange={(event) => setNewDiscipline(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addDiscipline();
                }
              }}
              placeholder="e.g. ACS"
              maxLength={40}
              className="h-9 rounded-control border-line bg-surface text-[13.5px] text-ink"
              aria-describedby="discipline-preview"
              data-testid="shop-drawing-discipline-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => addDiscipline()}
              disabled={!preview || alreadyActive}
              data-testid="add-shop-drawing-discipline-btn"
            >
              <Plus aria-hidden="true" /> Add
            </Button>
          </div>
          <p id="discipline-preview" className="mt-1.5 min-h-[18px] text-[12px] text-ink-3" aria-live="polite" data-testid="discipline-preview">
            {preview ? (
              preview === 'OTHER' ? (
                <><span className="font-mono text-ink-2">OTHER</span> is always active — it is the fallback for drawings without a discipline</>
              ) : alreadyActive ? (
                <><span className="font-mono text-ink-2">{preview}</span> is already active</>
              ) : (
                <>Will be saved as <span className="rounded border border-line bg-surface-2 px-1 font-mono text-[11.5px] font-semibold text-ink">{preview}</span> · press <kbd className="ui-kbd">Enter</kbd></>
              )
            ) : null}
          </p>
          {suggestions.length ? (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="discipline-suggestions">
              <span className="text-[12px] text-ink-3">Suggested:</span>
              {suggestions.map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => addDiscipline(code)}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-[color:var(--line-dashed)] bg-surface px-2.5 text-[12px] font-semibold text-ink-2 transition-colors hover:border-[color:var(--ink-4)] hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid={`suggest-discipline-${code}`}
                >
                  <Plus className="h-3 w-3" aria-hidden="true" /> {code}
                </button>
              ))}
            </div>
          ) : null}
        </SettingRow>
      </AdminCard>

      <AdminCard
        title="Workspace defaults"
        badge={<StatusPill tone="lime" label="New" size="sm" hideDot />}
        subtitle={currencyApplied
          ? 'The company’s time zone, currency, date format and working days.'
          : 'The company’s time zone, date format and working days. Currency follows later.'}
        bodyClassName="divide-y divide-line-2 py-1"
        data-testid="settings-card-workspace"
      >
        <SettingRow
          label="Time zone"
          hint="Every date and time in the app — History, Timeline, Forecast, tasks — is shown in this zone."
          htmlFor="workspace-time-zone"
        >
          <WorkspaceSelect
            id="workspace-time-zone"
            icon={Globe2}
            value={draft.workspace.time_zone}
            options={optionList('time_zone')}
            onChange={(value) => editWorkspace('time_zone', value)}
            disabled={settingsLoading}
            testId="workspace-time-zone"
          />
        </SettingRow>
        <SettingRow
          label="Currency"
          hint={currencyApplied ? 'Default for BOQ, POs and invoices.' : 'Not used by any page yet — amounts show in AED.'}
          htmlFor="workspace-currency"
        >
          <div className="flex items-center gap-2" data-testid="workspace-currency-row">
            <WorkspaceSelect
              id="workspace-currency"
              icon={Coins}
              value={draft.workspace.currency}
              options={optionList('currency')}
              onChange={(value) => editWorkspace('currency', value)}
              disabled={!currencyApplied || settingsLoading}
              testId="workspace-currency"
            />
            {currencyApplied ? null : <SoonPill />}
          </div>
        </SettingRow>
        <SettingRow label="Work week" hint="A project’s Timeline auto-plan starts with these working days.">
          <div className="max-w-full overflow-x-auto">
            <Segmented
              label="Work week"
              value={draft.workspace.work_week}
              onChange={(value) => editWorkspace('work_week', value)}
              options={optionList('work_week').map((option) => ({ value: option.value, label: option.label }))}
              testIdPrefix="workspace-work-week"
            />
          </div>
        </SettingRow>
        <SettingRow label="Date format" hint="How every date in the app is written." htmlFor="workspace-date-format">
          <div className="flex items-center gap-2" data-testid="workspace-date-format-row">
            <WorkspaceSelect
              id="workspace-date-format"
              icon={CalendarDays}
              value={draft.workspace.date_format}
              options={optionList('date_format')}
              onChange={(value) => editWorkspace('date_format', value)}
              describe={(option) => (
                <span className="flex items-center gap-2">
                  {option.example || option.label}
                  {option.example ? <span className="text-[12px] text-ink-3">{option.label}</span> : null}
                </span>
              )}
              disabled={settingsLoading}
              testId="workspace-date-format"
            />
          </div>
        </SettingRow>
      </AdminCard>

      <UnsavedBar
        count={dirtyCount}
        // the bar truncates a long summary: hovering it shows every change
        detail={changeText ? <span title={changeText} data-testid="settings-changes-detail">{changeText}</span> : ''}
        status={status === 'saved' && dirtyCount === 0 ? 'saved' : status}
        error={saveError}
      >
        <div className="ml-auto flex w-full items-center justify-end gap-2 sm:w-auto">
          <span className="mr-1 hidden items-center gap-1.5 whitespace-nowrap text-[12px] text-ink-3 lg:inline-flex" data-testid="settings-leave-hint">
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Leaving this tab will ask first
          </span>
          <Button type="button" variant="ghost" onClick={discard} disabled={saving} data-testid="discard-app-settings-btn">
            Discard
          </Button>
          <Button type="button" variant="primary" onClick={save} disabled={saving || dirtyCount === 0 || nameMissing} data-testid="save-app-settings-btn">
            {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}
            {saving ? 'Saving…' : status === 'error' ? 'Try again' : 'Save changes'}
          </Button>
        </div>
      </UnsavedBar>
    </div>
  );
}
